'use client';

import { useCart, useIsMounted, useLoyaltyContext, useUserSession } from '@libs/common-next';
import { getAccessToken, isLastActiveExpired } from '@libs/core';

export function useAccountDropdown() {
    const { cart } = useCart();
    const { memberPoints } = useLoyaltyContext();
    const { storageUserSession, userData, isSoftAuthenticated, user } = useUserSession();
    const { isMounted } = useIsMounted();

    // The `accessToken` cookie outlives the inactivity window — the session is only torn
    // down once `Auth0TokenSync` runs — while `firstName` comes from `memberPoints`, which
    // `LoyaltyProvider` seeds synchronously from `localStorage`. Treating the bare cookie
    // as signed in therefore paints the cached greeting before the teardown lands, and
    // drops it once the cookie goes.
    const hasAccessToken = !!getAccessToken() && !isLastActiveExpired();
    const hasTokenCookies = isMounted && hasAccessToken;
    const isPendingLoggedInState = !isMounted && hasAccessToken;
    const isLoggedIn = isPendingLoggedInState || hasTokenCookies || !!user;

    const firstName = (memberPoints?.firstName ?? userData?.profile?.firstName ?? '').trim();
    const loyaltyPoints = isSoftAuthenticated
        ? 0
        : (memberPoints?.availablePoints ?? storageUserSession?.loyaltyPoints ?? 0);
    const loyaltyPointsText = isSoftAuthenticated
        ? '****'
        : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(loyaltyPoints || 0);

    return {
        availableDollars: isSoftAuthenticated ? 0 : (memberPoints?.availableDollars ?? 0),
        currentMemberLevel: memberPoints?.currentTierLevel ?? 'member',
        isLoading: isPendingLoggedInState || (isLoggedIn && !firstName),
        isLoggedIn,
        isSoftAuthenticated,
        firstName,
        loyaltyPoints,
        loyaltyPointsText,
        qty: cart?.summary?.itemCount ?? undefined,
    };
}
